import React, { useState } from 'react'
import PropTypes from 'prop-types'

import { TextField } from '@mui/material'
import { cloneDeep } from 'lodash'
import { Button, DialogConfirm } from 'components'
import TextFieldFillBar from 'components/TextFieldFillBar'
import { toast } from 'react-toastify'

import { createOpenQuestion } from 'services/apiKYP'
import { useSelector } from 'react-redux'

import styles from './styles'

const initialValues = {
  subject: '',
  text: '',
}

const initialErrors = {
  subject: false,
  text: false,
}

const AdditionalQuestionForm = ({ applicationUuid, onSubmitQuestion = null }) => {
  const classes = styles()
  const min = 25
  const max = 1000

  const token = useSelector((state) => state.auth.access_token)

  const [formState, setFormState] = useState(initialValues)
  const [formErrors, setFormErrors] = useState(initialErrors)
  const [loading, setLoading] = useState(false)
  const [openOnSubmit, setOpenOnSubmit] = useState(false)
  const [openOnClean, setOpenOnClean] = useState(false)

  const handleTextFieldChange = (event) => {
    const newFormState = cloneDeep(formState)
    const key = event.target.id
    const { value } = event.target
    if (value.length <= max) {
      newFormState[key] = value
      setFormState(newFormState)
    }
  }

  const handleCloseDialog = () => {
    setOpenOnClean(false)
    setOpenOnSubmit(false)
  }

  const cleanForm = () => {
    setFormState(initialValues)
    setFormErrors(initialErrors)
    handleCloseDialog()
  }

  const handleSubmit = () => {
    setLoading(true)
    createOpenQuestion(token, applicationUuid, formState)
      .then(() => {
        toast.success('Pergunta enviada com sucesso')
        cleanForm()

        if (onSubmitQuestion) {
          onSubmitQuestion()
        }
      })
      .catch((error) => {
        toast.error(error?.response?.data?.detail)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const handleOpenOnSubmit = () => {
    setOpenOnSubmit(true)
  }

  const handleOpenOnClean = () => {
    setOpenOnClean(true)
  }

  const validateAnwers = () => {
    const newFormErrors = cloneDeep(initialErrors)
    const keys = Object.keys(initialErrors)

    keys.forEach((key) => {
      if (formState[key] === '') {
        newFormErrors[key] = true
      } else if (key === 'text' && (formState[key].length < min || formState[key].length > max)) {
        newFormErrors[key] = true
      }
    })

    setFormErrors(newFormErrors)

    if (keys.some((key) => newFormErrors[key])) {
      toast.error(
        `Verifique o preenchimento dos campos, os mesmos não podem estar vazios e o campo de texto não pode conter menos que ${min} caracteres`,
      )
    } else {
      handleOpenOnSubmit()
    }
  }

  const renderQuestionForm = () => (
    <div className={classes.formWrapper}>
      <TextField
        id="subject"
        size="small"
        label="Digite o assunto da questão"
        value={formState.subject}
        error={formErrors.subject}
        onChange={handleTextFieldChange}
        required
      />
      <TextField
        multiline
        size="small"
        id="text"
        label="Digite o texto da questão adicional"
        minRows={5}
        value={formState.text}
        error={formErrors.text}
        onChange={handleTextFieldChange}
        required
      />
      <TextFieldFillBar current={formState.text.length} min={min} max={max} />
      <div className={classes.buttonsContainer}>
        <Button onClick={handleOpenOnClean} variant="text" disabled={formState.text.length === 0}>
          Limpar Formulário
        </Button>
        <Button onClick={validateAnwers} loading={loading}>
          Enviar questão
        </Button>
      </div>
    </div>
  )

  const renderConfirmDialog = () => (
    <React.Fragment>
      <DialogConfirm
        open={openOnSubmit}
        onConfirm={handleSubmit}
        onCancel={handleCloseDialog}
        title="Tem certeza de que deseja enviar essa questão?"
        btnCancelText="cancelar"
        btnConfirmText="Enviar"
      >
        <div className={classes.questionConfirmation}>
          <p>
            <span>ATENÇÃO: </span>
            após o envio não será possível realizar nenhuma alteração.
          </p>
          <p>Por favor verifique o texto da questão e caso esteja correto clique em enviar.</p>
          <div>
            <p>
              <span>Assunto:</span>
              {` "${formState.subject}"`}
            </p>
            <p>
              <br></br>
              <span>Texto da questão:</span>
              {` "${formState.text}"`}
            </p>
          </div>
        </div>
      </DialogConfirm>
      <DialogConfirm
        open={openOnClean}
        onConfirm={cleanForm}
        onCancel={handleCloseDialog}
        title="Tem certeza de que deseja limpar o formulário?"
        btnCancelText="cancelar"
        btnConfirmText="Limpar"
      >
        Esta ação é irreversível e não poderá ser desfeita.
      </DialogConfirm>
    </React.Fragment>
  )

  return (
    <div className={classes.root}>
      <h3>Criar pergunta adicional</h3>

      {renderQuestionForm()}
      {renderConfirmDialog()}
    </div>
  )
}

AdditionalQuestionForm.propTypes = {
  applicationUuid: PropTypes.string.isRequired,
  onSubmitQuestion: PropTypes.func,
}

AdditionalQuestionForm.defaultProps = {
  onSubmitQuestion: null,
}

export default AdditionalQuestionForm
